import { useEffect, useMemo, useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import Filter from "./Filter";
import AddDoctorClinicRouter from "./Add";
import DeleteDoctorClinicRouter from "./Delete";
import { downloadCSV } from "services/doctorClinicRouter";
import { FormButton } from "components/Common/Buttons";
import Modal from "components/Common/Modal";
import { COLUMNS_DATA, FILTER_DEFINATIONS } from "./config";
import Table from "components/Common/Table/index";
import usePagination from "hooks/usePagination";
import useFilterHook from "hooks/useFilter";
import { DEFAULT_TOTAL_RECORDS, MODALS, STALE_TIME } from "utils/constants";
import useDownloadCSV from "hooks/useDownloadCSV";
import Loader from "components/Common/loader";
import { fetchDoctorClinicRouters } from "services/doctorClinicRouter";

const DoctorClinicRouter = () => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [rowDataForEdit, setRowDataForEdit] = useState(null);
  const [deleteId, setDeleteId] = useState(-1);

  const [isSuperFranchise] = useState(() =>
    Number(localStorage.getItem("is_super_franchise")),
  );
  const [isFranchiseEmployee] = useState(() =>
    Number(localStorage.getItem("is_franchise_employee")),
  );
  const [isSubFranchise] = useState(() =>
    Number(localStorage.getItem("sub_franchise")),
  );

  const {
    currentPage,
    itemPerPage,
    disablePrev,
    disableNext,
    disableFirst,
    disableLast,
    totalPages,
    updateCurrentPage,
    updateTotalRecords,
  } = usePagination();

  const DownloadButton = useDownloadCSV(
    downloadCSV,
    "doctor-clinic-router.csv",
  );

  const { filters, updateFilter, updateMultipleFilters } = useFilterHook(
    FILTER_DEFINATIONS,
    updateCurrentPage,
  );

  const handleEdit = useCallback((row) => {
    setRowDataForEdit(row);
    setIsAddModalOpen(true);
  }, []);

  const handleDelete = useCallback((row) => {
    setDeleteId(row.id);
  }, []);

  const {
    data: routerData,
    isLoading,
    refetch,
  } = useQuery(
    ["fetch-doctor-clinic-router", currentPage, filters],
    () => fetchDoctorClinicRouters(currentPage, filters),
    {
      staleTime: STALE_TIME,
      onSuccess: (res) => {
        updateTotalRecords(res?.data?.additional_data?.total_count);
      },
      select: (res) => {
        const response = res?.data?.data;
        if (response?.length) {
          res.data.data = response.map((item, index) => ({
            ...item,
            _index: (currentPage - 1) * DEFAULT_TOTAL_RECORDS + index + 1,
          }));
        }
        return res;
      },
      onError: (err) => {
        console.error("Error fetching doctor clinic routers:", err);
      },
    },
  );

  const onClose = useCallback(
    (shouldRefetch = false) => {
      if (shouldRefetch) refetch();
      setIsAddModalOpen(false);
      setRowDataForEdit(null);
    },
    [refetch],
  );

  const onCloseDelete = useCallback(
    (shouldRefetch = false) => {
      if (shouldRefetch) refetch();
      setDeleteId(-1);
    },
    [refetch],
  );

  const columns = useMemo(() => {
    return COLUMNS_DATA({
      deleteRow: handleDelete,
      editRow: handleEdit,
      isSuperFranchise,
      isFranchiseEmployee,
      isSubFranchise,
      filters,
    });
  }, [
    handleDelete,
    handleEdit,
    isSuperFranchise,
    isFranchiseEmployee,
    isSubFranchise,
    filters.is_active,
  ]);

  useEffect(() => {
    if (filters.text) {
      refetch();
    }
  }, [filters.text, refetch]);

  const loadNextPage = () => updateCurrentPage(currentPage + 1);
  const loadPreviousPage = () => updateCurrentPage(currentPage - 1);
  const loadFirstPage = () => currentPage !== 1 && updateCurrentPage(1);
  const loadLastPage = () =>
    currentPage !== totalPages && updateCurrentPage(totalPages);

  if (isLoading) return <Loader isLoading={isLoading} />;

  return (
    <div className="container-fluid mb-3">
      <div className="box mb-4">
        <div className="boxHeader">
          <h1>Doctor Clinic Router</h1>

          <div className="left d-lg-flex align-items-center">
            <Filter
              updateFilter={updateFilter}
              filters={filters}
              updateMultipleFilters={updateMultipleFilters}
            />
          </div>

          <DownloadButton />

          <FormButton
            onClick={() => setIsAddModalOpen(true)}
            button_name="Add Doctor Clinic Router"
          />

          <Modal
            type="modal-lg"
            show={isAddModalOpen}
            onHide={() => onClose(false)}
            modalName={MODALS.ADD_BRANCH_MODAL}
            title={
              rowDataForEdit
                ? "Edit Doctor Clinic Router"
                : "Add Doctor Clinic Router"
            }
            body={
              <AddDoctorClinicRouter
                rowData={rowDataForEdit}
                onClose={onClose}
              />
            }
          />

          <Modal
            show={deleteId !== -1}
            onHide={() => onCloseDelete()}
            modalName={MODALS.DELETE_BRANCH_MODAL}
            title="Are you sure you want to delete this router?"
            footer={
              <DeleteDoctorClinicRouter
                onClose={onCloseDelete}
                item={deleteId}
              />
            }
          />
        </div>

        <div className="boxBody">
          <Table
            data={routerData?.data?.data || []}
            isLoading={isLoading}
            tableColumns={columns}
            currentPage={currentPage}
            totalPages={totalPages}
            itemPerPage={itemPerPage}
            disablePrev={disablePrev}
            disableNext={disableNext}
            disableFirst={disableFirst}
            disableLast={disableLast}
            loadFirstPage={loadFirstPage}
            loadLastPage={loadLastPage}
            totalRecords={routerData?.data?.additional_data?.total_count}
            loadNextPage={loadNextPage}
            loadPreviousPage={loadPreviousPage}
          />
        </div>
      </div>
    </div>
  );
};

export default DoctorClinicRouter;
